import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Clapperboard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";

export default function Projects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", editorial_line: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Projeto criado");
      setOpen(false);
      setForm({ name: "", description: "", editorial_line: "" });
      await load();
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="flex items-center justify-between fade-up">
        <div>
          <h1 className="font-head font-700 text-4xl">Projetos</h1>
          <p className="text-[#A1A1AA] mt-2">Cada reality é um projeto com episódios, cenas e participantes.</p>
        </div>
        {user?.role === "producer" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="new-project-button"
                className="bg-[#FF2E93] hover:bg-[#ff54a6] text-white font-semibold rounded-full px-5 py-3 flex items-center gap-2 transition-colors duration-200"
              >
                <Plus className="w-4 h-4" /> Novo projeto
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#121218] border-[#282834] text-[#F4F4F5]">
              <DialogHeader>
                <DialogTitle className="font-head text-xl">Novo projeto</DialogTitle>
                <DialogDescription className="text-[#A1A1AA]">Defina o nome, a descrição e a linha editorial do reality.</DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4 mt-2">
                <input
                  data-testid="project-name-input"
                  value={form.name} onChange={set("name")} required placeholder="Nome do reality (ex.: Ilha do Fogo)"
                  className="w-full bg-[#08080C] border border-[#282834] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors duration-200"
                />
                <textarea
                  data-testid="project-description-input"
                  value={form.description} onChange={set("description")} placeholder="Descrição"
                  className="w-full bg-[#08080C] border border-[#282834] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors duration-200 resize-none h-20"
                />
                <textarea
                  data-testid="project-line-input"
                  value={form.editorial_line} onChange={set("editorial_line")}
                  placeholder="Linha editorial (tom do programa: drama, treta, romance, comédia...)"
                  className="w-full bg-[#08080C] border border-[#282834] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors duration-200 resize-none h-20"
                />
                <button
                  type="submit" disabled={saving}
                  data-testid="save-project-button"
                  className="w-full bg-[#FF2E93] hover:bg-[#ff54a6] text-white font-semibold rounded-full py-3 flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} Criar projeto
                </button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {projects.length === 0 ? (
        <div className="mt-10 bg-[#121218] border border-dashed border-[#282834] rounded-xl p-16 text-center">
          <Clapperboard className="w-10 h-10 text-[#282834] mx-auto mb-4" />
          <p className="text-[#A1A1AA]">
            {user?.role === "producer" ? "Crie seu primeiro projeto para começar." : "Nenhum projeto disponível ainda."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {projects.map((p) => (
            <button
              key={p.id}
              data-testid={`project-card-${p.id}`}
              onClick={() => navigate(`/projects/${p.id}`)}
              className="text-left bg-[#121218] border border-[#282834] rounded-xl overflow-hidden hover:-translate-y-1 hover:border-[#FF2E93] transition-all duration-200 group"
            >
              <div className="h-36 relative overflow-hidden">
                <img src={p.thumbnail} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121218] via-transparent to-transparent" />
              </div>
              <div className="p-5">
                <p className="font-head font-600 text-lg">{p.name}</p>
                <p className="text-sm text-[#A1A1AA] line-clamp-2 mt-1 min-h-[40px]">{p.description || "Sem descrição"}</p>
                <div className="flex gap-4 mt-4 text-xs text-[#A1A1AA] border-t border-[#282834] pt-3">
                  <span>{p.video_count} vídeos</span>
                  <span>{p.episode_count} episódios</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
